
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AuthContext } from "@/contexts/AuthContext";
import { useAuthMethods } from "@/hooks/useAuthMethods";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useSubscriptionNavigation } from "@/hooks/useSubscriptionNavigation";
import { useAuthState } from "@/hooks/useAuthState";
import { AuthLoadingState } from "./auth/AuthLoadingState";
import { AuthErrorState } from "./auth/AuthErrorState";
import { toast } from "sonner";
import { typecastSupabaseResult, hasProperty, safeGetProperty } from "@/utils/supabaseTypeUtils";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { 
    user, 
    session, 
    loading, 
    authTimeout, 
    loadingFailed, 
    retryAuth 
  } = useAuthState();
  
  const location = useLocation();
  const navigate = useNavigate();
  const { signIn, signUp, signOut, signInWithGoogle } = useAuthMethods();
  
  const { 
    data: subscriptionStatus, 
    isLoading: isSubscriptionLoading, 
    refetch: refetchSubscription 
  } = useSubscriptionStatus(loading ? undefined : user?.id);

  useEffect(() => {
    if (!user?.id) return; // Skip if no user
    
    let isMounted = true;
    
    const checkUrlParameters = async () => {
      if (!isMounted) return;
      
      console.log("🔍 Checking URL for parameters:", location.search);
      
      const urlParams = new URLSearchParams(location.search);
      const redirectPath = urlParams.get('redirect');
      
      if (redirectPath && redirectPath.includes('accept-invitation')) {
        console.log("📧 Detected invitation redirect:", redirectPath);
        
        const tokenMatch = redirectPath.match(/token=([^&]+)/);
        if (tokenMatch && tokenMatch[1]) {
          const token = decodeURIComponent(tokenMatch[1]);
          
          try {
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-invitation`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}`
              },
              body: JSON.stringify({
                userId: user.id,
                token: token
              })
            });
            
            const result = await response.json();
            
            if (result.success) {
              toast.success('You have successfully joined the team!');
              navigate('/dashboard', { replace: true });
              return;
            }
          } catch (error) {
            console.error("Error auto-accepting invitation:", error);
          }
        }
        
        navigate(redirectPath, { replace: true });
        return;
      }
      
      if (location.search.includes('checkout_success=true') || location.search.includes('success=true')) {
        console.log("✅ Checkout success detected in URL");
        toast.success('Subscription created successfully!');
        refetchSubscription();
        console.log("🔄 Refetching subscription status after successful checkout");
        
        navigate('/dashboard', { replace: true });
        return;
      }
      
      if (location.search.includes('upgrade_success=true')) {
        console.log("✅ Upgrade success detected in URL");
        toast.success('Subscription upgraded successfully!');
        refetchSubscription();
        console.log("🔄 Refetching subscription status after successful upgrade");
        
        navigate('/dashboard', { replace: true });
        return;
      }
      
      if (location.search.includes('canceled=true') || location.search.includes('upgrade_canceled=true')) {
        console.log("ℹ️ Cancellation detected in URL");
        toast.info('Subscription update was canceled');
        
        navigate('/dashboard', { replace: true });
        return;
      }
      
      if (location.search.includes('session_id=')) {
        console.log("ℹ️ Stripe session ID detected in URL");
        navigate('/dashboard', { replace: true });
        return;
      }
    };
    
    checkUrlParameters();
    
    return () => {
      isMounted = false;
    };
  }, [user?.id, location.search, navigate, refetchSubscription, session]);

  useEffect(() => {
    console.log("📡 Setting up auth state change listener");
    
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      console.log(`📡 Auth state changed: ${event}`);
      
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && newSession?.user) {
        console.log("🔄 User signed in, refreshing subscription data");
        refetchSubscription();
        
        setTimeout(async () => {
          try {
            const userEmail = newSession.user.email?.toLowerCase().trim();
            if (userEmail) {
              console.log("📧 Checking for pending invitations for email:", userEmail);
              
              const { data: invitations, error: invitationsError } = await supabase
                .from('team_members')
                .select('*')
                .eq('email', userEmail as any)
                .eq('status', 'pending' as any);
                
              if (invitationsError) {
                console.error("Error checking for pending invitations:", invitationsError);
              } else if (invitations && invitations.length > 0) {
                console.log("Found pending invitations");
                
                let activatedCount = 0;
                
                for (const invitation of invitations) {
                  try {
                    // Type cast to required update type and ensure id property exists
                    const typedInvitation = typecastSupabaseResult<{ id: string }>(invitation);
                    
                    if (!hasProperty(typedInvitation, 'id')) {
                      console.error("Invitation missing ID:", invitation);
                      continue;
                    }
                    
                    const updateData = {
                      user_id: newSession.user.id,
                      status: 'active',
                      invite_token: null,
                      updated_at: new Date().toISOString()
                    };
                    
                    const { error: updateError } = await supabase
                      .from('team_members')
                      .update(updateData as any)
                      .eq('id', typedInvitation.id);
                    
                    if (!updateError) {
                      activatedCount++;
                    }
                  } catch (activationError) {
                    const invitationId = hasProperty(invitation, 'id') ? safeGetProperty<string>(invitation, 'id') : 'unknown';
                    console.error(`Error activating invitation ${invitationId}:`, activationError);
                  }
                }
                
                if (activatedCount > 0) {
                  toast.success(`You've been added to ${activatedCount} team${activatedCount > 1 ? 's' : ''}!`);
                  console.log(`Successfully activated ${activatedCount} invitations`);
                }
              } else {
                console.log("No pending invitations found for email:", userEmail);
              }
            }
          } catch (error) {
            console.error("Error processing invitations in auth state change:", error);
          }
        }, 0);
      }
    });

    return () => {
      console.log("📡 Cleaning up auth state change listener");
      subscription?.unsubscribe();
    };
  }, [refetchSubscription]);

  useSubscriptionNavigation({
    loading,
    isSubscriptionLoading,
    user,
    subscriptionStatus
  });

  if (loading) {
    return <AuthLoadingState authTimeout={authTimeout} />;
  }

  if (loadingFailed) {
    return <AuthErrorState retryAuth={retryAuth} />;
  }
  
  const adaptedSignUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      await signUp(email, password, firstName, lastName);
    } catch (error) {
      console.error("Sign up error in AuthProvider:", error);
      throw error;
    }
  };
  
  const adaptedSignIn = async (email: string, password: string) => {
    await signIn(email, password);
  };
  
  const adaptedSignInWithGoogle = async () => {
    await signInWithGoogle();
  };
  
  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading, 
      signOut, 
      signUp: adaptedSignUp, 
      signIn: adaptedSignIn, 
      signInWithGoogle: adaptedSignInWithGoogle
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export { useAuth } from "@/contexts/AuthContext";
